/**
 * Organization Hierarchy Builder
 * Visual org chart with create/edit capabilities
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OrganizationHierarchy = () => {
  const [organizations, setOrganizations] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newOrg, setNewOrg] = useState({
    tenant_id: '',
    parent_id: null,
    name: '',
    code: '',
    type: 'branch',
    city: '',
    country: 'Ethiopia'
  });

  useEffect(() => {
    fetchTenants();
  }, []);

  useEffect(() => {
    if (selectedTenant) {
      fetchOrganizations(selectedTenant);
    }
  }, [selectedTenant]);

  const fetchTenants = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/tenants?tenant_type=bank`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTenants(response.data);
      if (response.data.length > 0) {
        setSelectedTenant(response.data[0].id);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchOrganizations = async (tenantId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/organizations?tenant_id=${tenantId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrganizations(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const createOrganization = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/organizations`, {
        ...newOrg,
        tenant_id: selectedTenant
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowCreateForm(false);
      setNewOrg({ tenant_id: '', parent_id: null, name: '', code: '', type: 'branch', city: '', country: 'Ethiopia' });
      fetchOrganizations(selectedTenant);
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const renderOrgTree = (orgs, parentId = null, level = 0) => {
    return orgs
      .filter(org => org.parent_id === parentId)
      .map(org => (
        <div key={org.id} style={{ marginLeft: level * 24 }} className="mb-2">
          <div className="flex items-center gap-2 p-3 bg-white rounded-lg shadow hover:shadow-md border-l-4 border-blue-500">
            <span className="text-lg">
              {org.type === 'bank_hq' ? '🏛️' : 
               org.type === 'branch' ? '🏪' : 
               org.type === 'department' ? '📁' : '📊'}
            </span>
            <div className="flex-1">
              <div className="font-semibold">{org.name}</div>
              <div className="text-sm text-gray-600">
                Level {org.level} | {org.type} | {org.code}
              </div>
              {org.hierarchy_path && (
                <div className="text-xs text-gray-500 mt-1">{org.hierarchy_path}</div>
              )}
            </div>
            <button
              onClick={() => {
                setNewOrg({ ...newOrg, parent_id: org.id, tenant_id: selectedTenant });
                setShowCreateForm(true);
              }}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              + Add Child
            </button>
          </div>
          {renderOrgTree(orgs, org.id, level + 1)}
        </div>
      ));
  };

  return (
    <div className="p-8">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Organization Hierarchy</h1>
          <p className="text-gray-600 mt-1">Build and manage organizational structure</p>
        </div>
        <button
          onClick={() => {
            setNewOrg({ ...newOrg, tenant_id: selectedTenant, parent_id: null });
            setShowCreateForm(true);
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          + Create Organization
        </button>
      </div>

      {/* Tenant Selector */}
      <div className="mb-6">
        <label className="block text-sm font-medium mb-2">Select Bank Tenant</label>
        <select
          value={selectedTenant}
          onChange={(e) => setSelectedTenant(e.target.value)}
          className="border rounded px-3 py-2 w-64"
        >
          {tenants.map(t => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      </div>

      {/* Organization Tree */}
      <div className="space-y-2">
        {organizations.length > 0 ? (
          renderOrgTree(organizations)
        ) : (
          <div className="text-center py-12 text-gray-500">
            No organizations yet. Click "Create Organization" to build your hierarchy.
          </div>
        )}
      </div>

      {/* Create Form Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Create Organization</h2>
            <form onSubmit={createOrganization}>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Name *</label>
                  <input
                    type="text"
                    value={newOrg.name}
                    onChange={(e) => setNewOrg({...newOrg, name: e.target.value})}
                    className="w-full border rounded px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Code *</label>
                  <input
                    type="text"
                    value={newOrg.code}
                    onChange={(e) => setNewOrg({...newOrg, code: e.target.value})}
                    className="w-full border rounded px-3 py-2"
                    placeholder="HQ, BR-001, etc."
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Type *</label>
                  <select
                    value={newOrg.type}
                    onChange={(e) => setNewOrg({...newOrg, type: e.target.value})}
                    className="w-full border rounded px-3 py-2"
                  >
                    <option value="bank_hq">Bank HQ</option>
                    <option value="region">Region</option>
                    <option value="branch">Branch</option>
                    <option value="department">Department</option>
                    <option value="cost_center">Cost Center</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">City</label>
                  <input
                    type="text"
                    value={newOrg.city}
                    onChange={(e) => setNewOrg({...newOrg, city: e.target.value})}
                    className="w-full border rounded px-3 py-2"
                    placeholder="Addis Ababa"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
                >
                  Create
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="bg-gray-200 px-6 py-2 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationHierarchy;